import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Camera, Check, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { NewsShareCard } from "../components/NewsShare";
import { WaiverForm } from "../components/WaiverForm";

export default function PublicGuestUpload() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [w, setW] = useState(null);
  const [name, setName] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const key = `lif_shared_${slug}`;
  const [shared, setShared] = useState(() => { try { return JSON.parse(sessionStorage.getItem(key)) || []; } catch { return []; } });
  useEffect(() => { sessionStorage.setItem(key, JSON.stringify(shared)); }, [key, shared]);
  const fileRef = useRef();
  const wkey = `lif_waiver_${slug}`;
  const [waiver, setWaiver] = useState(() => localStorage.getItem(wkey) || "");

  useEffect(() => {
    api.get(`/public/weddings/${slug}`).then((r) => setW(r.data)).catch(() => {});
  }, [slug]);

  async function pick(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("uploader_name", name || "Guest");
        fd.append("caption", caption);
        fd.append("waiver_token", waiver);
        if (sp.get("table")) fd.append("table_id", sp.get("table"));
        const { data } = await api.post(`/public/weddings/${slug}/memories`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (String(data.content_type).startsWith("image/")) setShared((s) => [data, ...s].slice(0, 6));
        setCount((c) => c + 1);
      }
      toast.success("Memories uploaded");
      e.target.value = "";
    } catch (e) {
      if (e?.response?.status === 403) { localStorage.removeItem(wkey); setWaiver(""); toast.error("Please sign the photo release again"); }
      else toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }

  if (!w) return null;
  return (
    <div data-testid="public-guest-upload" className="min-h-screen bg-[var(--bg)] py-12 px-6">
      <div className="max-w-md mx-auto text-center">
        {sp.get("table") && <Link to={`/site/${slug}/table/${sp.get("table")}`} className="block text-xs text-[var(--muted)] mb-4 underline" data-testid="upload-back-to-table">Back to your table</Link>}
        <Heart className="w-6 h-6 text-[var(--gold)] mx-auto mb-3" />
        <p className="font-script text-[var(--gold)] text-3xl">{w.couple_names}</p>
        <h1 className="font-serif font-light text-3xl mt-4 mb-3">Share your photos with us</h1>
        <p className="text-[var(--muted)] text-sm mb-8">Upload anything from the day — moments, candids, dance floor magic. The couple will love them.</p>

        {!waiver ? (
          <div className="bg-white border border-[var(--line)] rounded-2xl p-6 text-left" data-testid="guest-waiver">
            <WaiverForm drawn withEmail cta="Sign & start uploading"
              submit={async (b) => (await api.post(`/public/weddings/${slug}/waiver`, b)).data}
              onSigned={(d) => { localStorage.setItem(wkey, d.token); setWaiver(d.token); if (!name) setName(d.full_name || ""); }} />
          </div>
        ) : (
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 text-left space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Your name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="input-luxe" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Headline / caption (optional)</label>
            <input value={caption} maxLength={120} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Best man caught crying during the vows!" className="input-luxe" data-testid="guest-caption-input" />
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="guest-upload-btn" className="btn-gold w-full">
            <Camera className="w-4 h-4" /> {busy ? "Uploading…" : "Choose Photos / Videos"}
          </button>
          <input ref={fileRef} onChange={pick} type="file" multiple accept="image/*,video/*" className="hidden" />
          {count > 0 && <div className="flex items-center justify-center gap-2 text-sm text-[var(--gold)]"><Check className="w-4 h-4" /> {count} uploaded — keep them coming!</div>}
        </div>
        )}

        {shared.length > 0 && (
          <div className="mt-8 space-y-4" data-testid="news-share-list">
            <p className="font-serif text-2xl">Share it like breaking news</p>
            {shared.map((it) => <NewsShareCard key={it.item_id} slug={slug} item={it} defaultHeadline={`${it.uploaded_by_name || "A guest"} spotted celebrating at ${w.couple_names}'s wedding!`} />)}
            <Link to={`/site/${slug}/news`} target="_blank" rel="noreferrer" className="block text-center text-xs text-[var(--gold)] underline" data-testid="upload-feed-link">See all the party news and heart your favourites</Link>
          </div>
        )}

        {w.hashtag && <p className="font-script text-3xl text-[var(--gold)] mt-8">{w.hashtag}</p>}
      </div>
    </div>
  );
}
